import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { Job } from './ClientJobPortalContent';

interface QuoteAcceptanceCardProps {
  job: Job;
  onAccept: () => void;
  onDecline: () => void;
}

const mockQuoteLines = [
  { id: 'ql-001', description: 'Lawn mow & edge (standard block)', qty: 1, unit: 'service', rate: 85.00 },
  { id: 'ql-002', description: 'Garden bed weeding (×2 beds)', qty: 2, unit: 'bed', rate: 35.00 },
  { id: 'ql-003', description: 'Hedge trimming & shaping', qty: 1, unit: 'service', rate: 110.00 },
  { id: 'ql-004', description: 'Green waste removal & disposal', qty: 1, unit: 'load', rate: 60.00 },
];

export default function QuoteAcceptanceCard({ job, onAccept, onDecline }: QuoteAcceptanceCardProps) {
  const subtotal = mockQuoteLines.reduce((sum, l) => sum + l.qty * l.rate, 0);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  return (
    <div className="border-2 border-amber-200 bg-amber-50 rounded-xl p-4 space-y-4">
      <div className="flex items-start gap-2">
        <Icon name="EnvelopeOpenIcon" size={18} variant="outline" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Quote received — action required</p>
          <p className="text-xs text-amber-700 mt-0.5">{job.notes}</p>
        </div>
      </div>

      {/* Quote lines */}
      <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
        <div className="px-3 py-2 bg-amber-100 grid grid-cols-4 text-xs font-semibold text-amber-800">
          <span className="col-span-2">Description</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Total</span>
        </div>
        {mockQuoteLines.map((line) => (
          <div key={line.id} className="px-3 py-2 grid grid-cols-4 text-xs border-t border-amber-100">
            <span className="col-span-2 text-foreground">{line.description}</span>
            <span className="text-right text-muted-foreground font-tabular">{line.qty}</span>
            <span className="text-right text-foreground font-tabular">${(line.qty * line.rate).toFixed(2)}</span>
          </div>
        ))}
        <div className="px-3 py-2 border-t border-amber-200 space-y-0.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Subtotal</span>
            <span className="font-tabular">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>GST (10%)</span>
            <span className="font-tabular">${gst.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold text-foreground pt-1 border-t border-amber-100">
            <span>Total (inc. GST)</span>
            <span className="font-tabular text-primary">${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onAccept}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all active:scale-95"
        >
          <Icon name="CheckIcon" size={15} variant="outline" />
          Accept Quote
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="px-4 py-2.5 border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-all"
        >
          Decline
        </button>
      </div>
    </div>
  );
}